import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import { useDispatch } from 'react-redux';
import {
  REVIEW_CYCLE_STEPS_V1,
  setIsAllEmployeesSelected,
  setIsRevieweesCustomViewsSettings,
  setSelectedEmployeeCount,
  setReviewees,
  setTotalEmployeeCount,
  useIsAllEmployeesSelected,
  useIsRevieweesCustomViewsSettings,
  useReviewCreationData,
  useReviewees,
} from 'reducers/reviewCreationSlice';

import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { useReviewCreationNavigation } from '../useReviewCreationNavigation';

const getReviewees = ({ reviewCycleId, step }) => {
  const url = endpoints.reviews.reviewCycleCreation.reviewees;
  return sendRequest({
    url,
    credentials: 'include',
    queryParams: {
      review_cycle_id: reviewCycleId,
      step,
    },
  });
};

const putReviewees = ({ reviewCycleId, views, hasCustomSettings, isAllEmployeesSelected }) => {
  const url = endpoints.reviews.reviewCycleCreation.reviewees;
  return sendRequest({
    url,
    method: 'PUT',
    credentials: 'include',
    queryParams: {
      review_cycle_id: reviewCycleId,
    },
    body: {
      cohorts: isAllEmployeesSelected ? null : views?.map((v) => ({ id: v.id, name: v.name })),

      hasCustomSettings,
      isAllEmployeesSelected,
    },
  });
};

export const useGetReviewees = ({ reviewCycleId, step = REVIEW_CYCLE_STEPS_V1.REVIEWEES }) => {
  const dispatch = useDispatch();
  return useQuery(
    [REACT_QUERY_KEYS.reviewCreation.REVIEWEES, step],
    () =>
      getReviewees({ reviewCycleId, step }).then((res) => {
        const { cohorts, hasCustomSettings, isAllEmployeesSelected, orgEmployeeCount, selectedEmployeeCount } =
          res.entity;
        if (isAllEmployeesSelected) {
          dispatch(setIsAllEmployeesSelected(true));
        } else {
          dispatch(setReviewees(cohorts));
        }
        dispatch(setTotalEmployeeCount(orgEmployeeCount));
        dispatch(setSelectedEmployeeCount(selectedEmployeeCount));
        dispatch(setIsRevieweesCustomViewsSettings(hasCustomSettings));

        return res.entity;
      }),
    { enabled: !!reviewCycleId }
  );
};

export const useSaveReviewees = () => {
  const { goToNextStep, goToStep } = useReviewCreationNavigation();
  const { reviewCycleId } = useReviewCreationData();

  const views = useReviewees();
  const isAllEmployeesSelected = useIsAllEmployeesSelected();
  const hasCustomSettings = useIsRevieweesCustomViewsSettings();

  return useMutation({
    mutationFn: () =>
      putReviewees({
        reviewCycleId,
        views,
        hasCustomSettings,
        isAllEmployeesSelected,
      }),
    onSuccess: (_data, variables) => {
      // todo: navigate to next step
      if (variables?.nextStep) {
        goToStep(variables?.nextStep);
        return;
      }
      goToNextStep();
      // --------------------------------
      // queryClient.invalidateQueries([REACT_QUERY_KEYS.views.views]);
      // queryClient
      //   .invalidateQueries([REACT_QUERY_KEYS.views.sidebarViewCohorts, module])
      //   .then(() => {
      //     applyFilters(history, location, data);
      //   });
      // // applyFilters(history, location, data);
      // dispatch(setSaveViewErrorMessage(null));
      // dispatch(setIsSaveViewModalOpen(false));
      // // dispatch(setViewsSubScreen(null));
    },
    onError: () => {
      // if (error?.message) {
      //   dispatch(setSaveViewErrorMessage(error.message));
      // }
      // dispatch(setIsSaveViewModalOpen(false));
    },
  });
};

export const getReviewViewsList = () => {
  const url = endpoints.reviews.reviewCycleCreation.viewsList;
  return sendRequest({
    url,
    credentials: 'include',
  });
};

export const useGetReviewViewsList = () =>
  useQuery([REACT_QUERY_KEYS.reviewCreation.views], () => getReviewViewsList().then((res) => res.entity));

export const getViewEmployees = ({ viewId }) => {
  const url = endpoints.reviews.reviewCycleCreation.cohortEmployees;
  return sendRequest({
    url,
    credentials: 'include',
    queryParams: {
      cohort_id: viewId,
    },
  });
};

export const useGetViewEmployees = ({ viewId }) =>
  useQuery(
    [REACT_QUERY_KEYS.reviewCreation.views, viewId],
    () =>
      getViewEmployees({ viewId }).then((res) =>
        res.entity.map((e) => ({
          employee: e,
          designation: e.designation,
          department: e.employeeFunction,
        }))
      ),
    { enabled: !!viewId, staleTime: 60000 }
  );

export const useInvalidateViewEmployees = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: [REACT_QUERY_KEYS.reviewCreation.views],
    });
};

export const getOverlappingViewEmployees = ({ views = [] }) => {
  const url = endpoints.reviews.reviewCycleCreation.overlappingViewEmployees;
  return sendRequest({
    url,
    method: 'POST',
    credentials: 'include',
    body: views.map((v) => v.id),
  });
};

export const useGetOverlappingViewEmployees = ({ views = [], isRevieweesCustomViewsSettings }) =>
  useQuery(
    [REACT_QUERY_KEYS.reviewCreation.overlappingViewEmployees, views.map((v) => v.id).join(',')],
    () =>
      getOverlappingViewEmployees({ views }).then((res) =>
        res.entity.employeeCohortList.map((e) => ({
          employee: e.employeeDetails,
          views: e.cohorts,
        }))
      ),
    { enabled: views.length > 1 && isRevieweesCustomViewsSettings }
  );

export const useInvalidateOverlappingViewEmployees = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: [REACT_QUERY_KEYS.reviewCreation.overlappingViewEmployees],
    });
};
